import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { useCompanyUsersV2 } from '@/hooks/useCandidatesV2';

const STAGES = ['applied','resume_parsed','eligible','exam_assigned','exam_completed','shortlisted','interview_scheduled','offer','hired','rejected'];
const STAGE_LABELS = {
  applied: 'Applied', resume_parsed: 'Resume Parsed', eligible: 'Eligible',
  exam_assigned: 'Exam Assigned', exam_completed: 'Exam Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  offer: 'Offer', hired: 'Hired', rejected: 'Rejected',
};

const SOURCES = ['manual', 'linkedin', 'referral', 'imported'];
const SOURCE_LABELS = { manual: 'Manual', linkedin: 'LinkedIn', referral: 'Referral', imported: 'Imported' };

const EMPTY = { stage: '', owner: '', source: '', status: '', dateFrom: '', dateTo: '' };

const CandidateFiltersV2 = ({ filters = EMPTY, onApply, onClear }) => {
  const [local, setLocal] = useState({ ...EMPTY, ...filters });
  const { data: usersData } = useCompanyUsersV2();
  const users = Array.isArray(usersData) ? usersData : (usersData?.users || []);

  useEffect(() => { setLocal({ ...EMPTY, ...filters }); }, [filters]);

  const set = (key, val) => setLocal((p) => ({ ...p, [key]: val === 'all' ? '' : val }));

  const handleApply = () => onApply(local);
  const handleClear = () => { setLocal(EMPTY); onClear(); };

  return (
    <div className="bg-white border rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex flex-wrap gap-4 items-end">
        {/* Stage */}
        <div className="space-y-1 min-w-[140px]">
          <Label className="text-xs text-gray-500">Stage</Label>
          <Select value={local.stage || 'all'} onValueChange={(v) => set('stage', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Owner */}
        <div className="space-y-1 min-w-[160px]">
          <Label className="text-xs text-gray-500">Owner</Label>
          <Select value={local.owner || 'all'} onValueChange={(v) => set('owner', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="All owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {users.map((u) => (
                <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Source */}
        <div className="space-y-1 min-w-[130px]">
          <Label className="text-xs text-gray-500">Source</Label>
          <Select value={local.source || 'all'} onValueChange={(v) => set('source', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {SOURCES.map((s) => (
                <SelectItem key={s} value={s}>{SOURCE_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status */}
        <div className="space-y-1 min-w-[120px]">
          <Label className="text-xs text-gray-500">Status</Label>
          <Select value={local.status || 'all'} onValueChange={(v) => set('status', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date From */}
        <div className="space-y-1 min-w-[140px]">
          <Label className="text-xs text-gray-500">Created From</Label>
          <Input
            type="date"
            className="h-8 text-sm"
            value={local.dateFrom}
            onChange={(e) => set('dateFrom', e.target.value)}
          />
        </div>

        {/* Date To */}
        <div className="space-y-1 min-w-[140px]">
          <Label className="text-xs text-gray-500">Created To</Label>
          <Input
            type="date"
            className="h-8 text-sm"
            value={local.dateTo}
            onChange={(e) => set('dateTo', e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 items-end pb-0.5">
          <Button size="sm" className="h-8 text-xs" onClick={handleApply}>
            Apply Filters
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={handleClear}>
            <X className="h-3 w-3" />
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateFiltersV2;
